/**
 * @file WanxiangUpload.tsx
 * @description 通义万相wan2.6模型专用上传组件
 *
 * 功能特性：
 * - 支持上传图片、视频、音频三种文件类型
 * - 配置面板（智能改写、分辨率、视频时长、镜头模式、生成音频）
 * - 文件互斥逻辑处理（视频与图片/音频互斥，图片和音频可共存）
 * - 根据上传文件类型动态显示配置项（文生视频/图生视频/视频生视频模式）
 * - 点击外部区域自动关闭配置面板
 *
 * 使用场景：
 * - 文生视频模式：仅输入文字提示词，或文字+音频
 * - 图生视频模式：上传图片，或图片+音频
 * - 视频生视频模式：上传视频
 */
import { showMessage } from '@/components/antd-comp/message'
import { showError } from '@/components/antd-comp/message/useNotification'
import axios from '@/controllers/request'
import type { UploadProps } from 'antd'
import { Tooltip, Upload } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import SwitchComponent from '../../antd-comp/switch'
import '../css/wanxiangUpload.less'

/**
 * 通义万相模型配置项接口
 */
interface WanxiangConfig {
  smartRewrite: boolean // 智能改写，默认开启
  resolution: string // 分辨率，默认1080P，可选720P
  duration: string // 视频时长，默认5秒，可选10秒、15秒（视频生视频模式仅支持5秒、10秒）
  cameraMode: string // 镜头模式，默认单镜头，可选多镜头
  generateAudio: boolean // 生成音频，默认生成
}

/**
 * 通义万相上传组件属性接口
 */
interface WanxiangUploadProps {
  running: boolean // 是否正在运行（禁用上传）
  assistantId: string // 助手ID
  currentConversationId: string // 当前会话ID
  setUpIsLoading: (loading: boolean) => void // 设置加载状态
  logoUrl: string // 上传的图片URL
  setLogoUrl: (url: string) => void // 设置图片URL
  setOriginalUrl: (url: string) => void // 设置原始图片URL
  setImgName: (name: string) => void // 设置图片名称
  UploadProps: UploadProps // Ant Design Upload组件的属性
  uploadedVideos: Array<{ type: string; url: string; name: string }> // 已上传的视频列表（最多3个）
  setUploadedVideos: React.Dispatch<React.SetStateAction<Array<{ type: string; url: string; name: string }>>> // 设置视频列表
  uploadedAudio: { type: string; url: string; name: string } | null // 已上传的音频（最多1个）
  setUploadedAudio: (audio: { type: string; url: string; name: string } | null) => void // 设置音频
  wanxiangConfig: WanxiangConfig // 通义万相配置项
  setWanxiangConfig: (config: WanxiangConfig | ((prev: WanxiangConfig) => WanxiangConfig)) => void // 设置配置项
}

export default function WanxiangUpload({
  running,
  assistantId,
  currentConversationId,
  setUpIsLoading,
  logoUrl,
  setLogoUrl,
  setOriginalUrl,
  setImgName,
  UploadProps,
  uploadedVideos,
  setUploadedVideos,
  uploadedAudio,
  setUploadedAudio,
  wanxiangConfig,
  setWanxiangConfig
}: WanxiangUploadProps) {
  // 配置面板打开/关闭状态
  const [configPanelOpen, setConfigPanelOpen] = useState(false)
  // 配置面板容器的引用，用于检测点击外部区域
  const configPanelRef = useRef<HTMLDivElement>(null)

  // ========== 串行上传队列管理 ==========
  /**
   * 上传队列：（一个文件成功后再上传下一个）
   * 队列项包含：文件、成功回调、失败回调
   */
  interface UploadQueueItem {
    file: File
    onSuccess: (response: any) => void
    onError: (error: any) => void
  }
  const uploadQueueRef = useRef<UploadQueueItem[]>([])
  const isUploadingRef = useRef<boolean>(false)
  // 组件是否已卸载标记（用于在组件卸载时停止上传）
  const isMountedRef = useRef<boolean>(true)
  // AbortController 用于取消正在进行的 HTTP 请求
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * 处理上传队列：串行执行，一个文件成功后再处理下一个
   */
  const processUploadQueue = async () => {
    // 如果正在上传或队列为空，直接返回
    if (isUploadingRef.current || uploadQueueRef.current.length === 0) {
      return
    }

    // 如果组件已卸载，清空队列并返回
    if (!isMountedRef.current) {
      uploadQueueRef.current = []
      return
    }

    // 标记为正在上传
    isUploadingRef.current = true
    setUpIsLoading(true)

    try {
      // 循环处理队列中的文件（串行执行）
      while (uploadQueueRef.current.length > 0) {
        // 每次循环前检查组件是否已卸载
        if (!isMountedRef.current) {
          // 组件已卸载，清空队列并停止处理
          uploadQueueRef.current = []
          break
        }

        const item = uploadQueueRef.current.shift()!

        // 如果组件已卸载，跳过这个文件
        if (!isMountedRef.current) {
          continue
        }

        // 如果还没有 AbortController，创建一个
        if (!abortControllerRef.current) {
          abortControllerRef.current = new AbortController()
        }

        try {
          // 使用支持取消的上传函数
          const formData = new FormData()
          formData.append('file', item.file)

          const response = await axios.post('/api/v1/common/upload_file', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'chat-alias-id': currentConversationId,
              'assistant-id': assistantId,
              'X-Skip-Global-Error': 'true' // 跳过全局错误处理，我们自己处理取消错误
            },
            signal: abortControllerRef.current.signal // 传入 signal 以支持取消
          })

          // 上传成功后，再次检查组件是否已卸载，避免更新已卸载组件的状态
          if (isMountedRef.current) {
            item.onSuccess(response)
          }
        } catch (err: any) {
          // 如果组件已卸载，静默处理，不报错
          if (!isMountedRef.current) {
            console.log('组件已卸载，停止上传')
            uploadQueueRef.current = []
            break
          }

          // 判断是否是手动取消的请求（AbortController 取消）
          // 关键：只有没有 response 的取消错误才是手动取消的
          const hasResponse = err?.response !== undefined
          const errorMessage = err?.message || err?.toString() || ''
          const errorCode = err?.code || err?.name || ''

          // 手动取消的判断：没有 response 且是取消相关的错误
          const isManualCancel =
            !hasResponse &&
            (errorCode === 'ERR_CANCELED' ||
              errorCode === 'ECONNABORTED' ||
              errorCode === 'CanceledError' ||
              errorMessage.includes('canceled') ||
              errorMessage.includes('aborted') ||
              errorMessage.includes('(canceled)'))

          if (isManualCancel) {
            // 手动取消的请求，静默处理，不调用错误回调
            console.log('上传请求已手动取消', { errorCode, errorMessage })
            uploadQueueRef.current = []
            break
          }

          // 后端返回的错误（有 response），正常报错
          console.error('上传失败（后端错误）:', err)
          if (isMountedRef.current) {
            item.onError(err)
            // X-Skip-Global-Error 跳过了全局拦截器的 toast，这里补上业务错误提示
            const errMsg = typeof err === 'string' ? err : (err?.message || '上传失败')
            showError('上传失败', errMsg)
          }
        }
      }
    } finally {
      // 所有文件上传完成（无论成功或失败）
      isUploadingRef.current = false
      // 只有在组件未卸载时才更新 loading 状态
      if (isMountedRef.current) {
        setUpIsLoading(false)
      }
    }
  }

  /**
   * 将文件添加到上传队列（串行上传）
   */
  const addToUploadQueue = (file: File, onSuccess: (response: any) => void, onError: (error: any) => void) => {
    uploadQueueRef.current.push({ file, onSuccess, onError })
    // 如果当前没有正在上传的文件，开始处理队列
    processUploadQueue()
  }

  // ========== 模式判断 ==========
  // 判断是否上传了视频
  const hasVideo = uploadedVideos.length > 0
  // 判断是否上传了音频
  const hasAudio = !!uploadedAudio
  // 判断是否上传了图片
  const hasImage = !!logoUrl

  // 视频生视频模式：上传了视频
  const isVideoToVideoMode = hasVideo

  // ========== 监听文件变化，自动重置配置 ==========
  /**
   * 记录上一次的文件状态，用于判断文件变化类型
   */
  const prevFileStateRef = useRef<{
    hasVideo: boolean
    hasImage: boolean
    hasAudio: boolean
  }>({
    hasVideo: false,
    hasImage: false,
    hasAudio: false
  })

  /**
   * 当文件状态发生变化时，根据具体变化类型调整配置
   * 
   * 规则：
   * 1. 只上传了图片（之前没有图片，也没有音频）：生成音频-不生成、智能改写-开启、镜头模式-单镜头、分辨率-1080、视频时长-5s
   * 2. 只上传了音频（之前没有音频，也没有图片）：生成音频-生成、智能改写-开启、镜头模式-单镜头、分辨率-1080、视频时长-5s
   * 3. 上传了图片，已经有音频了：什么都不需要改
   * 4. 上传了音频，已经有图片了：生成音频-生成
   * 5. 上传了视频：配置-单镜头、智能改写-隐藏、生成音频-生成、分辨率-1080、视频时长-5s
   * 6. 删除了视频，没有任何文件：还原配置
   * 7. 删除了视频，还有视频：什么都不需要改
   * 8. 删除了图片，还有音频：什么都不需要改
   * 9. 删除了音频，还有图片：生成音频-不生成
   * 10. 删除了音频或图片，没有任何文件：还原配置
   */
  useEffect(() => {
    const prev = prevFileStateRef.current
    const current = {
      hasVideo,
      hasImage,
      hasAudio
    }

    // 判断文件变化类型
    const imageAdded = !prev.hasImage && current.hasImage // 图片从无到有
    const audioAdded = !prev.hasAudio && current.hasAudio // 音频从无到有
    const videoAdded = !prev.hasVideo && current.hasVideo // 视频从无到有
    const videoRemoved = prev.hasVideo && !current.hasVideo // 视频从有到无
    const imageRemoved = prev.hasImage && !current.hasImage // 图片从有到无
    const audioRemoved = prev.hasAudio && !current.hasAudio // 音频从有到无
    const hasNoFile = !current.hasVideo && !current.hasImage && !current.hasAudio // 没有任何文件

    // 1. 只上传了图片（之前没有图片，也没有音频）
    if (imageAdded && !prev.hasAudio && !prev.hasVideo) {
      setWanxiangConfig({
        resolution: '1080P',
        duration: '5',
        cameraMode: 'single',
        smartRewrite: true,
        generateAudio: false
      })
    }
    // 2. 只上传了音频（之前没有音频，也没有图片）
    else if (audioAdded && !prev.hasImage && !prev.hasVideo) {
      setWanxiangConfig({
        resolution: '1080P',
        duration: '5',
        cameraMode: 'single',
        smartRewrite: true,
        generateAudio: true
      })
    }
    // 3. 上传了图片，已经有音频了：什么都不需要改（不处理）
    // 4. 上传了音频，已经有图片了：生成音频-生成
    else if (audioAdded && prev.hasImage && !prev.hasVideo) {
      setWanxiangConfig(prev => ({ ...prev, generateAudio: true }))
    }
    // 5. 上传了视频：配置-单镜头、智能改写-隐藏、生成音频-生成、分辨率-1080、视频时长-5s
    else if (videoAdded) {
      setWanxiangConfig({
        resolution: '1080P',
        duration: '5',
        cameraMode: 'single',
        smartRewrite: true, // 注意：视频模式下智能改写应该隐藏，但这里设置为true，因为隐藏是UI层面的
        generateAudio: true
      })
    }
    // 6. 删除了视频，没有任何文件：还原配置
    else if (videoRemoved && hasNoFile) {
      setWanxiangConfig({
        resolution: '1080P',
        duration: '5',
        cameraMode: 'single',
        smartRewrite: true,
        generateAudio: false
      })
    }
    // 7. 删除了视频，还有视频：什么都不需要改（不处理）
    // 8. 删除了图片，还有音频：什么都不需要改（不处理）
    // 9. 删除了音频，还有图片：生成音频-不生成
    else if (audioRemoved && current.hasImage && !current.hasVideo) {
      setWanxiangConfig(prev => ({ ...prev, generateAudio: false }))
    }
    // 10. 删除了音频或图片，没有任何文件：还原配置
    else if ((audioRemoved || imageRemoved) && hasNoFile) {
      setWanxiangConfig({
        resolution: '1080P',
        duration: '5',
        cameraMode: 'single',
        smartRewrite: true,
        generateAudio: false
      })
    }

    // 更新记录的状态
    prevFileStateRef.current = current
  }, [hasVideo, hasAudio, hasImage, setWanxiangConfig])

  // ========== 点击外部区域关闭配置面板 ==========
  /**
   * 监听点击事件，当点击配置面板外部区域时，自动关闭面板
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // 检查点击的元素是否在 tooltip 内部（tooltip 通过 portal 渲染在 body 下）
      const tooltipElement = (target as Element)?.closest?.('.ant-tooltip')
      if (tooltipElement) {
        // 如果点击的是 tooltip 内容，不关闭配置面板
        return
      }

      // 如果点击的元素不在配置面板容器内，则关闭面板
      if (configPanelRef.current && !configPanelRef.current.contains(target)) {
        setConfigPanelOpen(false)
      }
    }

    // 仅在面板打开时添加事件监听
    if (configPanelOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    // 组件卸载或面板关闭时，移除事件监听
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [configPanelOpen])

  // ========== 组件卸载时清理上传队列 ==========
  /**
   * 当组件卸载或切换应用/路由时，清理上传队列并停止上传
   */
  useEffect(() => {
    // 组件挂载时标记为已挂载
    isMountedRef.current = true

    // 组件卸载时清理
    return () => {
      // 标记组件已卸载
      isMountedRef.current = false

      // 取消正在进行的 HTTP 请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }

      // 清空上传队列
      uploadQueueRef.current = []
      // 重置上传状态
      isUploadingRef.current = false
      // 取消 loading 状态
      setUpIsLoading(false)
    }
  }, [])

  // ========== 文件上传处理 ==========
  /**
   * 通义万相专用上传逻辑（串行上传）
   * 图片、视频、音频都使用此接口：/api/v1/common/upload_file
   *
   * 注意：此函数返回 Promise，但实际是异步添加到队列中
   * 对于单个文件上传（图片、音频），可以直接 await
   * 对于多个文件上传（视频），会串行执行
   */
  const handleUpload = async (file: File): Promise<any> => {
    return new Promise((resolve, reject) => {
      addToUploadQueue(
        file,
        response => resolve(response),
        error => reject(error)
      )
    })
  }

  return (
    <div className="flexRowStart relative">
      {/* ========== 上传图片（通义万相专用） ========== */}
      {/* 
        规则：
        1. 格式限制：JPEG、JPG、PNG、BMP、WEBP
        2. 大小限制：不超过10MB
        3. 数量限制：最多1张
        4. 互斥规则：上传视频后不能再上传图片；图片和音频可以共存
      */}
      <Tooltip
        title={
          <div>
            <div>1. 上传图片，可根据图片生成视频；</div>
            <div>2. 格式限制：JPEG、JPG、PNG、BMP、WEBP；</div>
            <div>3. 图片大小限制：不超过10MB。</div>
            <div>4. 图像分辨率：图像的宽度和高度范围为[360, 2000]，单位为像素。</div>
          </div>
        }
        placement="top"
      >
        <div
          className="cursonCase h-[20px] w-[20px]"
          style={{
            opacity: uploadedVideos.length > 0 || running ? 0.5 : 1,
            cursor: uploadedVideos.length > 0 || running ? 'not-allowed' : 'pointer'
          }}
        >
          <Upload
            accept=".jpeg,.jpg,.png,.bmp,.webp"
            showUploadList={false}
            disabled={running || uploadedVideos.length > 0}
            beforeUpload={file => {
              // 检查：如果已上传视频，则不允许上传图片
              if (uploadedVideos.length > 0) {
                showMessage({
                  content: '上传视频后不能再上传图片',
                  type: 'warning'
                })
                return Upload.LIST_IGNORE
              }
              // 检查：如果已上传图片，则不允许再次上传
              if (logoUrl) {
                showMessage({
                  content: '最多上传一张图片',
                  type: 'warning'
                })
                return Upload.LIST_IGNORE
              }
              // 检查：文件格式限制（JPEG、JPG、PNG、BMP、WEBP）
              const allowedImageTypes = ['.jpeg', '.jpg', '.png', '.bmp', '.webp']
              const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
              if (!allowedImageTypes.includes(fileExtension)) {
                showMessage({
                  content: '图片格式不支持，仅支持 JPEG、JPG、PNG、BMP、WEBP 格式',
                  type: 'warning'
                })
                return Upload.LIST_IGNORE
              }
              // 检查：文件大小不能超过10MB
              if (file.size > 10 * 1024 * 1024) {
                showMessage({
                  content: '图片大小不能超过10MB',
                  type: 'warning'
                })
                return Upload.LIST_IGNORE
              }
              return true
            }}
            customRequest={async options => {
              const { file, onSuccess, onError } = options
              try {
                // 调用上传接口
                const response = await handleUpload(file as File)
                // 返回数据结构：{ file_path, crop_file_path, file_name }
                setLogoUrl(response?.file_path)
                setOriginalUrl(response?.file_path)
                setImgName((file as File).name)
                // 上传图片后，只清空视频（图片和音频可以共存，不清空音频）
                setUploadedVideos([])
                onSuccess({ file })
              } catch (err) {
                onError(err as any)
              }
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 17L13 9L17 13"
                stroke="#131921"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11 3H4C3.44772 3 3 3.44772 3 4V16C3 16.5523 3.44772 17 4 17H16C16.5523 17 17 16.5523 17 16V9"
                stroke="#131921"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="7.5" cy="7.5" r="1.5" fill="#131921" />
              <path d="M15 3L19 3" stroke="#131921" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M17 5L17 1" stroke="#131921" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Upload>
        </div>
      </Tooltip>
      {/* ========== 上传视频 ========== */}
      {/* 
        规则：
        1. 格式限制：mp4、mov
        2. 时长限制：2~30s
        3. 大小限制：单个视频不超过100MB
        4. 数量限制：最多3个视频
        5. 互斥规则：上传视频后不能再上传图片和音频；上传图片或音频后不能再上传视频
      */}
      <Tooltip
        title={
          <div>
            <div>1. 上传视频，可根据视频生成视频，最多上传3个视频；</div>
            <div>2. 格式限制：mp4、mov；时长限制：2~30s；</div>
            <div>3. 文件大小：单个视频不超过100MB。</div>
          </div>
        }
        placement="top"
      >
        <div
          className="cursonCase mx-[8px] h-[20px] w-[20px]"
          style={{
            opacity: logoUrl || uploadedAudio || running ? 0.5 : 1,
            cursor: logoUrl || uploadedAudio || running ? 'not-allowed' : 'pointer'
          }}
        >
          <Upload
            accept=".mp4,.mov"
            multiple
            maxCount={3}
            showUploadList={false}
            disabled={running || !!logoUrl || !!uploadedAudio}
            beforeUpload={file => {
              // 检查：最多上传3个视频
              if (uploadedVideos.length >= 3) {
                showMessage({
                  content: '不可上传超过3个视频',
                  type: 'warning'
                })
                return Upload.LIST_IGNORE
              }
              // 检查：文件格式限制（mp4、mov）
              const allowedVideoTypes = ['.mp4', '.mov']
              const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
              if (!allowedVideoTypes.includes(fileExtension)) {
                showMessage({
                  content: '视频格式不支持，仅支持 MP4、MOV 格式',
                  type: 'warning'
                })
                return Upload.LIST_IGNORE
              }
              // 检查：文件大小不能超过100MB
              if (file.size > 100 * 1024 * 1024) {
                showMessage({
                  content: '视频大小不能超过100MB',
                  type: 'warning'
                })
                return Upload.LIST_IGNORE
              }
              return true
            }}
            customRequest={async options => {
              const { file, onSuccess, onError } = options
              try {
                // 调用上传接口
                const response = await handleUpload(file as File)
                // 构建视频对象
                const newVideo = {
                  type: 'video',
                  url: response?.file_path,
                  name: response?.file_name || (file as File).name
                }
                // 添加到视频列表（支持多个视频）
                setUploadedVideos((prev: Array<{ type: string; url: string; name: string }>) => {
                  const updated = [...prev, newVideo]
                  return updated
                })
                // 上传视频后，清空图片和音频（视频与图片/音频互斥）
                setLogoUrl('')
                setOriginalUrl('')
                setImgName('')
                setUploadedAudio(null)
                onSuccess({ file })
              } catch (err) {
                onError(err as any)
              }
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M11.4286 3H5C4.44772 3 4 3.44772 4 4V16C4 16.5523 4.44772 17 5 17H16C16.5523 17 17 16.5523 17 16V9"
                stroke="#131921"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 7.5L2.15777 6.57889C1.62585 6.31292 1 6.69972 1 7.29443V11.7056C1 12.3003 1.62585 12.6871 2.15777 12.4211L4 11.5"
                stroke="black"
                strokeWidth="1.5"
              />
              <path
                d="M8 12.2841V8.07196C8 7.43768 8.70286 7.05565 9.23512 7.40064L12.8456 9.74073C13.3605 10.0745 13.3234 10.8398 12.7787 11.1223L9.16825 12.9943C8.63573 13.2705 8 12.884 8 12.2841Z"
                fill="#131921"
              />
              <path d="M15 3L19 3" stroke="#131921" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M17 5L17 1" stroke="#131921" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Upload>
        </div>
      </Tooltip>
      {/* ========== 上传音频 ========== */}
      {/* 
        规则：
        1. 格式限制：wav、mp3
        2. 时长限制：3～30s
        3. 大小限制：不超过15MB
        4. 数量限制：最多1个音频
        5. 互斥规则：上传视频后不能再上传音频；上传音频后不能再上传视频；音频和图片可以共存
      */}
      <Tooltip
        title={
          <div>
            <div>1. 上传音频，可根据此音频生成视频声音；</div>
            <div>2. 格式限制：wav、mp3；时长限制：3～30s；</div>
            <div>3. 文件大小：不超过15MB。</div>
          </div>
        }
        placement="top"
      >
        <div
          className="cursonCase h-[20px] w-[20px]"
          style={{
            opacity: uploadedVideos.length > 0 || running ? 0.5 : 1,
            cursor: uploadedVideos.length > 0 || running ? 'not-allowed' : 'pointer'
          }}
        >
          <Upload
            accept=".wav,.mp3"
            showUploadList={false}
            disabled={running || uploadedVideos.length > 0}
            beforeUpload={file => {
              // 检查：如果已上传音频，则不允许再次上传
              if (uploadedAudio) {
                showMessage({
                  content: '最多上传一份音频文件',
                  type: 'warning'
                })
                return Upload.LIST_IGNORE
              }
              // 检查：文件格式限制（wav、mp3）
              const allowedAudioTypes = ['.wav', '.mp3']
              const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
              if (!allowedAudioTypes.includes(fileExtension)) {
                showMessage({
                  content: '音频格式不支持，仅支持 WAV、MP3 格式',
                  type: 'warning'
                })
                return Upload.LIST_IGNORE
              }
              // 检查：文件大小不能超过15MB
              if (file.size > 15 * 1024 * 1024) {
                showMessage({
                  content: '音频大小不能超过15MB',
                  type: 'warning'
                })
                return Upload.LIST_IGNORE
              }
              return true
            }}
            customRequest={async options => {
              const { file, onSuccess, onError } = options
              try {
                // 调用上传接口
                const response = await handleUpload(file as File)
                // 设置音频（音频和图片可以共存，不清空图片）
                setUploadedAudio({
                  type: 'audio',
                  url: response?.file_path,
                  name: response?.file_name || (file as File).name
                })
                onSuccess({ file })
              } catch (err) {
                onError(err as any)
              }
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M11 3H4C3.44772 3 3 3.44772 3 4V16C3 16.5523 3.44772 17 4 17H16C16.5523 17 17 16.5523 17 16V9"
                stroke="#131921"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line x1="6.25" y1="8.75" x2="6.25" y2="12.25" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="13.75" y1="8.75" x2="13.75" y2="11.25" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="8.75" y1="7.75" x2="8.75" y2="13.25" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="11.25" y1="5.75" x2="11.25" y2="14.25" stroke="black" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M15 3L19 3" stroke="#131921" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M17 5L17 1" stroke="#131921" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Upload>
        </div>
      </Tooltip>
      <span className="configPanel_divider mx-[8px]"></span>

      {/* ========== 智能改写按钮 ========== */}
      {/* 
        规则：
        - 仅在非视频生视频模式显示（文生视频、图生视频模式）
        - 视频生视频模式下此配置项被隐藏
        - 默认开启
      */}
      {uploadedVideos.length === 0 && (
        <div className="flexRowStart ">
          <span className="wangxiangRewrite">智能改写</span>
          <Tooltip
            title={
              <div>
                <div>是否开启prompt智能改写。</div>
                <div>开启后使用大模型对输入prompt进行智能改写。</div>
                <div>对于较短的prompt生成效果提升明显，但会增加耗时。</div>
                <div>视频生视频模式下此参数会被强制关闭。</div>
              </div>
            }
          >
            <svg
              className="cursonCase mx-[4px]"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <circle cx="8" cy="8" r="7" fill="#A8ADB3" />
              <path
                d="M8.09492 4.04004C7.32492 4.04004 6.70492 4.27004 6.24492 4.71004C5.77492 5.16004 5.54492 5.78004 5.54492 6.55004C5.54492 6.58004 5.54492 6.89004 5.76492 7.13004C5.86492 7.24004 6.05492 7.36004 6.36492 7.36004C6.96492 7.36004 7.21492 6.89004 7.23492 6.56004C7.23492 6.23004 7.29492 5.98004 7.41492 5.82004C7.48492 5.72004 7.62492 5.58004 8.03492 5.58004C8.27492 5.58004 8.44492 5.64004 8.55492 5.74004C8.67492 5.86004 8.73492 6.04004 8.73492 6.25004C8.73492 6.41004 8.67492 6.57004 8.55492 6.71004L8.47492 6.81004C7.81492 7.41004 7.44492 7.82004 7.30492 8.10004C7.15492 8.39004 7.08492 8.73004 7.08492 9.16004V9.29004C7.08492 9.56004 7.30492 9.96004 7.91492 9.96004C8.54492 9.96004 8.75492 9.54004 8.78492 9.29004V9.16004C8.78492 8.99004 8.81492 8.84004 8.89492 8.69004C8.96492 8.55004 9.06492 8.43004 9.19492 8.32004C9.61492 7.95004 9.91492 7.68004 10.0449 7.53004C10.3149 7.17004 10.4549 6.72004 10.4549 6.19004C10.4549 5.53004 10.2349 5.00004 9.79492 4.61004C9.35492 4.24004 8.79492 4.04004 8.09492 4.04004Z"
                fill="white"
              />
              <path
                d="M7.0752 11.0897C7.0752 11.3205 7.16686 11.5418 7.33001 11.7049C7.49317 11.8681 7.71446 11.9597 7.9452 11.9597C8.17593 11.9597 8.39722 11.8681 8.56038 11.7049C8.72353 11.5418 8.8152 11.3205 8.8152 11.0897C8.8152 10.859 8.72353 10.6377 8.56038 10.4745C8.39722 10.3114 8.17593 10.2197 7.9452 10.2197C7.71446 10.2197 7.49317 10.3114 7.33001 10.4745C7.16686 10.6377 7.0752 10.859 7.0752 11.0897Z"
                fill="white"
              />
            </svg>
          </Tooltip>
          <div onClick={e => e.stopPropagation()} className="applicationEdit_switchCase">
            <SwitchComponent
              checked={wanxiangConfig.smartRewrite}
              onChange={e => {
                setWanxiangConfig(prev => ({ ...prev, smartRewrite: e }))
              }}
              size="small"
              disabled={running}
            />
          </div>
        </div>
      )}
      {uploadedVideos.length === 0 && <span className="configPanel_divider mx-[8px]"></span>}

      {/* ========== 配置按钮和配置面板 ========== */}
      {/* 
        显示当前配置的摘要信息，点击图标展开/收起详细配置面板
        配置项包括：
        - 智能改写（仅在非视频生视频模式显示）
        - 分辨率（1080P/720P）
        - 视频时长（5秒/10秒/15秒，视频生视频模式仅支持5秒/10秒）
        - 镜头模式（单镜头/多镜头）
        - 生成音频（生成/不生成，上传音频或视频生视频模式时强制为"生成"且不可更改）
      */}
      {
        <div
          className={`configPanel_chat flexRowStart relative ${configPanelOpen ? 'configPanel_chat_active' : ''}`}
          ref={configPanelRef}
        >
          <span
            className="cursonCase user-select-none"
            onClick={() => {
              if (!running) {
                setConfigPanelOpen(!configPanelOpen)
              }
            }}
          >
            {wanxiangConfig.resolution}
          </span>
          <span className="configPanel_chat_divider"></span>
          <span
            className="cursonCase user-select-none"
            onClick={() => {
              if (!running) {
                setConfigPanelOpen(!configPanelOpen)
              }
            }}
          >
            {wanxiangConfig.duration}s
          </span>
          <span className="configPanel_chat_divider"></span>
          <span
            className="cursonCase user-select-none"
            onClick={() => {
              if (!running) {
                setConfigPanelOpen(!configPanelOpen)
              }
            }}
          >
            {wanxiangConfig.cameraMode === 'single' ? '单镜头' : '多镜头'}
          </span>
          <span className="configPanel_chat_divider"></span>
          <div className="configPanel_chat_svg_container">
            {/* 默认显示的 SVG */}
            <svg
              className={`configPanel_chat_svg_default cursonCase ${running ? 'cursor-not-allowed opacity-50' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
            >
              <line
                x1="0.75"
                y1="3.01636"
                x2="13.25"
                y2="3.01636"
                stroke="#131921"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path d="M11 0.5L11 5.16667" stroke="#131921" strokeLinecap="round" />
              <line
                x1="0.75"
                y1="12.3499"
                x2="13.25"
                y2="12.3499"
                stroke="#131921"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path d="M11 9.83325L11 14.4999" stroke="#131921" strokeLinecap="round" />
              <line
                x1="0.75"
                y1="7.68311"
                x2="13.25"
                y2="7.68311"
                stroke="#131921"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path d="M3 5.16675L3 9.83341" stroke="#131921" strokeLinecap="round" />
            </svg>
            {/* hover 时显示的 SVG */}
            <svg
              onClick={() => {
                if (running) {
                  setConfigPanelOpen(false)
                  return
                }
                setConfigPanelOpen(!configPanelOpen)
              }}
              className={`configPanel_chat_svg_hover cursonCase ${running ? 'cursor-not-allowed opacity-50' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
            >
              <line
                x1="0.75"
                y1="3.0166"
                x2="13.25"
                y2="3.0166"
                stroke="#5964F3"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path d="M11 0.5L11 5.16667" stroke="#5964F3" strokeLinecap="round" />
              <line
                x1="0.75"
                y1="12.3496"
                x2="13.25"
                y2="12.3496"
                stroke="#5964F3"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path d="M11 9.8335L11 14.5002" stroke="#5964F3" strokeLinecap="round" />
              <line
                x1="0.75"
                y1="7.68311"
                x2="13.25"
                y2="7.68311"
                stroke="#5964F3"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path d="M3 5.1665L3 9.83317" stroke="#5964F3" strokeLinecap="round" />
            </svg>
          </div>
          {/* ========== 配置面板 ========== */}
          {configPanelOpen && (
            <div className="configPanel absolute bottom-[55px] left-0">
              {/* 标题 */}
              <span className="configPanel_title user-select-none mb-[16px] flex">生成设置</span>

              {/* ========== 分辨率配置 ========== */}
              {/* 默认1080P，可选720P */}
              <div className="flexRowStart flexRowJustBet mb-[16px]">
                <span>分辨率</span>
                <div className="configPanel_case flexRowStart">
                  {['1080P', '720P'].map(res => (
                    <div
                      key={res}
                      onClick={() => setWanxiangConfig(prev => ({ ...prev, resolution: res }))}
                      className={`configPanel_item flexRow ${
                        wanxiangConfig.resolution === res ? 'configPanel_item_active' : ''
                      }`}
                    >
                      {res}
                    </div>
                  ))}
                </div>
              </div>

              {/* ========== 视频时长配置 ========== */}
              {/* 
                规则：
                - 视频生视频模式：仅支持5秒、10秒两档
                - 文生视频/图生视频模式：支持5秒、10秒、15秒三档
                - 默认5秒
              */}
              <div className="flexRowStart flexRowJustBet mb-[16px]">
                <span>视频时长</span>
                <div className="configPanel_case flexRowStart">
                  {/* 根据模式动态显示选项 2个3个显示区分className */}
                  {(isVideoToVideoMode ? ['5', '10'] : ['5', '10', '15']).map(dur => (
                    <div
                      key={dur}
                      onClick={() => setWanxiangConfig(prev => ({ ...prev, duration: dur }))}
                      className={`${isVideoToVideoMode ? 'configPanel_item' : 'configPanel_item_thr'} flexRow ${
                        wanxiangConfig.duration === dur && isVideoToVideoMode
                          ? 'configPanel_item_active'
                          : wanxiangConfig.duration === dur
                          ? 'configPanel_item_thr_active'
                          : ''
                      }`}
                    >
                      {dur}s
                    </div>
                  ))}
                </div>
              </div>

              {/* ========== 镜头模式配置 ========== */}
              {/* 默认单镜头，可选多镜头 */}
              <div className="flexRowStart flexRowJustBet mb-[16px]">
                <span>镜头模式</span>
                <div className="configPanel_case flexRowStart">
                  {[
                    { value: 'single', label: '单镜头' },
                    { value: 'multi', label: '多镜头' }
                  ].map(mode => (
                    <div
                      key={mode.value}
                      onClick={() => setWanxiangConfig(prev => ({ ...prev, cameraMode: mode.value }))}
                      className={`configPanel_item flexRow ${
                        wanxiangConfig.cameraMode === mode.value ? 'configPanel_item_active' : ''
                      }`}
                    >
                      {mode.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* ========== 生成音频配置 ========== */}
              {/* 
                规则：
                - 如果上传了音频文件，则强制为"生成"，不可变更
                - 如果处于视频生视频模式，则强制为"生成"，不可变更
                - 如果没有上传音频且非视频生视频模式，则可以手动调节，默认值为"生成"
              */}
              <div className="flexRowStart flexRowJustBet">
                <span className="flexRowStart">
                  生成音频
                  <Tooltip title="上传音频文件或者视频文件即可生成视频音效" placement="top">
                    <svg
                      className="cursonCase ml-[4px]"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <circle cx="8" cy="8" r="7" fill="#A8ADB3" />
                      <path
                        d="M8.09492 4.04004C7.32492 4.04004 6.70492 4.27004 6.24492 4.71004C5.77492 5.16004 5.54492 5.78004 5.54492 6.55004C5.54492 6.58004 5.54492 6.89004 5.76492 7.13004C5.86492 7.24004 6.05492 7.36004 6.36492 7.36004C6.96492 7.36004 7.21492 6.89004 7.23492 6.56004C7.23492 6.23004 7.29492 5.98004 7.41492 5.82004C7.48492 5.72004 7.62492 5.58004 8.03492 5.58004C8.27492 5.58004 8.44492 5.64004 8.55492 5.74004C8.67492 5.86004 8.73492 6.04004 8.73492 6.25004C8.73492 6.41004 8.67492 6.57004 8.55492 6.71004L8.47492 6.81004C7.81492 7.41004 7.44492 7.82004 7.30492 8.10004C7.15492 8.39004 7.08492 8.73004 7.08492 9.16004V9.29004C7.08492 9.56004 7.30492 9.96004 7.91492 9.96004C8.54492 9.96004 8.75492 9.54004 8.78492 9.29004V9.16004C8.78492 8.99004 8.81492 8.84004 8.89492 8.69004C8.96492 8.55004 9.06492 8.43004 9.19492 8.32004C9.61492 7.95004 9.91492 7.68004 10.0449 7.53004C10.3149 7.17004 10.4549 6.72004 10.4549 6.19004C10.4549 5.53004 10.2349 5.00004 9.79492 4.61004C9.35492 4.24004 8.79492 4.04004 8.09492 4.04004Z"
                        fill="white"
                      />
                      <path
                        d="M7.0752 11.0897C7.0752 11.3205 7.16686 11.5418 7.33001 11.7049C7.49317 11.8681 7.71446 11.9597 7.9452 11.9597C8.17593 11.9597 8.39722 11.8681 8.56038 11.7049C8.72353 11.5418 8.8152 11.3205 8.8152 11.0897C8.8152 10.859 8.72353 10.6377 8.56038 10.4745C8.39722 10.3114 8.17593 10.2197 7.9452 10.2197C7.71446 10.2197 7.49317 10.3114 7.33001 10.4745C7.16686 10.6377 7.0752 10.859 7.0752 11.0897Z"
                        fill="white"
                      />
                    </svg>
                  </Tooltip>
                </span>
                <div className="configPanel_case flexRowStart">
                  {[
                    { value: true, label: '生成' },
                    { value: false, label: '不生成' }
                  ].map(mode => {
                    // 判断是否禁用：始终禁用（默认禁用，上传音频/视频时也禁用）
                    const isDisabled = true
                    // 判断当前选项是否为激活状态
                    const isActive = wanxiangConfig.generateAudio === mode.value
                    // 判断是否为禁用且非激活状态（需要显示禁用样式）
                    const isDisabledAndNotActive = isDisabled && !isActive
                    return (
                      <div
                        key={mode.value.toString()}
                        onClick={() => {
                          // 只有在非禁用状态下才能点击切换
                          if (!isDisabled) {
                            setWanxiangConfig(prev => ({ ...prev, generateAudio: mode.value }))
                          }
                        }}
                        className={`configPanel_item flexRow ${isActive ? 'configPanel_item_active' : ''} ${
                          isDisabledAndNotActive
                            ? 'configPanel_item_disabled cursor-not-allowed'
                            : 'cursonCase'
                        }`}
                      >
                        {mode.label}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      }
    </div>
  )
}
